/**
 * StaffReportDoc — printable Staff Weekly document (Claude Design "Reports"
 * handoff, pages 3–6). Job sheet grouped by day + the payout stack.
 *
 * Design only — the existing pay rules are untouched:
 *  - Hourly pay is days × wage; the per-job PAYOUT column is what the worker's
 *    jobs generated. That production figure is ADMIN-ONLY and never printed
 *    (class `admin-only-col reports-no-print`, same as the old table).
 *  - Commissioned workers are paid per job, so their payout column is theirs
 *    to see and does print.
 *
 * Employee shape comes from api.getStaffWeekly():
 *   { employee_name, is_hourly, jobs[{date, stock_number, model, service,
 *     department, units, payout, status}], weekly_pay, daily_wage, days_count,
 *     weekly_hours, total_payout, productivity, prev_weeks }
 */

import React from 'react';
import { ACTS_COMPANY } from '../config/company';
import {
  INK, NAVY, ORANGE, DAY_TINT, T1, T2, T3, T5, MONO, NUM,
  money, dayLabel, longDate, isoWeekRange, plural,
  th, td, MetaCell, Masthead, GroupBar, TotalsStack, DocFooter, DayBar,
} from './reportKit';

const initials = (name) => String(name || '').trim().split(/\s+/).map(w => w[0]).join('').slice(0, 3).toUpperCase();
const fmtQty = (n) => (Math.round(n * 100) / 100).toFixed(2);

// "4 days" when every clocked day was a full one, "3.5 of 5 days" when they
// weren't — paid days first, because that is the number being multiplied.
export function wageBasisLabel(emp) {
  const paid = Math.round((Number(emp.days_worked) || 0) * 100) / 100;
  const clocked = emp.days_count == null ? null : Number(emp.days_count);
  if (clocked != null && Math.abs(clocked - paid) > 0.005) {
    return `${paid} of ${clocked} days`;
  }
  return plural(paid, 'day');
}

export default function StaffReportDoc({ emp, week, year, isAdmin = true, extras = [] }) {
  const hourly = emp.is_hourly === true;
  const jobs = emp.jobs || [];

  const extrasTotal = extras.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
  const totalPayout = (Number(emp.total_payout) || 0) + extrasTotal;
  const production = jobs.reduce((s, j) => s + (parseFloat(j.payout) || 0), 0);
  const totalUnits = jobs.reduce((s, j) => s + (parseFloat(j.units) || 1), 0);

  // Group by day.
  const byDay = new Map();
  for (const j of jobs) {
    const k = j.date || '';
    if (!byDay.has(k)) byDay.set(k, []);
    byDay.get(k).push(j);
  }
  const days = [...byDay.keys()].sort();

  const svcCounts = {};
  jobs.forEach(j => { svcCounts[j.service] = (svcCounts[j.service] || 0) + (parseFloat(j.units) || 1); });

  const hasPartial = jobs.some(j => (parseFloat(j.units) || 1) < 1);
  const kicker = `Staff Weekly · ${hourly ? 'Hourly' : 'Commissioned'}`;
  const ref = `REF ACTS-W${week}-${initials(emp.employee_name)}-${year}`;

  // Totals stack: hourly shows the wage basis, commissioned the jobs subtotal.
  const lines = hourly
    ? [
        // The days here must be the days the money was built from. It used to
        // print days_count — the number of dates clocked — while the pay came
        // from days_worked, the sum of the day fractions. A week with one half
        // day then read "5 days × $228.00 = $798.00", which is not true, and a
        // wage line that doesn't multiply makes the whole page look wrong.
        //
        // When the two differ, both are shown: "3.5 of 5 days" says plainly
        // that a clocked day paid less than a full one, which is usually a
        // short day and occasionally a broken clock-in worth chasing.
        { label: `Base wage · ${wageBasisLabel(emp)} × ${money((emp.daily_wage || 0))}`, value: Number(emp.weekly_pay) || 0 },
        ...(extrasTotal > 0 ? [{ label: 'Extra payments', value: extrasTotal, green: true }] : []),
      ]
    : [
        { label: 'Jobs subtotal', value: Number(emp.total_payout) || 0 },
        ...(extrasTotal > 0 ? [{ label: 'Extra payments', value: extrasTotal, green: true }] : []),
      ];

  // The per-job payout column: hidden for hourly on print / for non-admin.
  const showPayoutCol = hourly ? isAdmin : true;
  const payoutColClass = hourly ? 'admin-only-col reports-no-print' : undefined;

  return (
    <div className="deptdoc" style={{ background: '#fff', color: T1, padding: '38px 40px 30px', display: 'flex', flexDirection: 'column' }}>

      <Masthead
        kicker={kicker}
        subject={emp.employee_name.toUpperCase()}
        seal={hourly ? 'HR' : 'CM'}
        period={`Week ${week} · ${isoWeekRange(week, year)}`}
        totalLabel="Total payout"
        total={totalPayout}
        reference={ref}
      />

      <div style={{ display: 'flex', borderTop: `3px solid ${INK}`, borderBottom: '1px solid #dcdcdc', padding: '10px 0' }}>
        <MetaCell first label="From" value={ACTS_COMPANY.name} sub={`ABN ${ACTS_COMPANY.abn} · ${ACTS_COMPANY.address}`} />
        <MetaCell label="Engagement" value={hourly ? 'Hourly contractor' : 'Commissioned contractor'}
          sub={hourly ? `${plural(emp.days_count ?? Math.round(emp.days_worked || 0), 'day')} worked` : 'Paid per job'} />
        <MetaCell label="Issued" value={longDate(new Date())} sub="Raise discrepancies before the pay run" />
        <MetaCell last label="Counts" value={`${plural(jobs.length, 'job')} · ${fmtQty(totalUnits)} units`}
          sub={plural(Object.keys(svcCounts).length, 'service type')} />
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 16 }}>
        <thead>
          <tr>
            <th style={{ ...th, width: 84 }}>Stock</th>
            <th style={th}>Model</th>
            <th className="svc-cell" style={th}>Job description</th>
            <th style={{ ...th, width: 92 }}>Department</th>
            <th style={{ ...th, width: 48, textAlign: 'right' }}>Qty</th>
            {showPayoutCol && <th className={payoutColClass} style={{ ...th, width: 78, textAlign: 'right' }}>Payout</th>}
          </tr>
        </thead>
        <tbody>
          {days.map(d => {
            const rows = byDay.get(d);
            const dayUnits = rows.reduce((s, j) => s + (parseFloat(j.units) || 1), 0);
            const dayPayout = rows.reduce((s, j) => s + (parseFloat(j.payout) || 0), 0);
            return (
              <React.Fragment key={d}>
                <GroupBar accent={NAVY} tint={DAY_TINT}>
                  <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: 1.1, color: INK, ...NUM }}>{dayLabel(d)}</span>
                  <span style={{ fontSize: 9.5, fontWeight: 600, color: '#6b7280' }}>
                    {plural(rows.length, 'job')} · {fmtQty(dayUnits)} units
                  </span>
                  <span style={{ flex: 1 }} />
                  {/* Day production — admin-only for hourly (same rule as the
                      payout column), so the printed copy shows neither. */}
                  {showPayoutCol && (
                    <span className={payoutColClass} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                      {hourly && <DayBar production={dayPayout} wage={emp.daily_wage || 228} />}
                      <span style={{ fontSize: 11, fontWeight: 800, color: INK, ...NUM }}>{money(dayPayout)}</span>
                    </span>
                  )}
                </GroupBar>

                {rows.map((r, i) => {
                  const u = parseFloat(r.units) || 1;
                  return (
                    <tr key={i}>
                      <td style={{ ...td, fontFamily: MONO, fontWeight: 700, letterSpacing: 0.5 }}>{r.stock_number}</td>
                      <td style={{ ...td, fontWeight: 600 }}>{r.model}</td>
                      <td className="svc-cell" style={{ ...td, whiteSpace: 'nowrap' }}>{r.service}</td>
                      <td style={{ ...td, color: T3 }}>{r.department}</td>
                      <td style={{ ...td, textAlign: 'right', ...NUM, ...(u < 1 ? { color: ORANGE, fontWeight: 700 } : {}) }}>{fmtQty(u)}</td>
                      {showPayoutCol && (
                        <td className={payoutColClass} style={{ ...td, textAlign: 'right', fontWeight: 700, ...NUM }}>{money(r.payout || 0)}</td>
                      )}
                    </tr>
                  );
                })}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>

      <TotalsStack lines={lines} grandLabel="TOTAL PAYOUT" grandValue={totalPayout} />

      {/* Production generated — hourly only, and admin-only (never printed). */}
      {hourly && isAdmin && (
        <div className="admin-only-col reports-no-print" style={{ width: 290, marginLeft: 'auto', display: 'flex', justifyContent: 'space-between', background: '#f7f7f7', padding: '6px 12px', marginTop: 2 }}>
          <span style={{ fontSize: 10, fontWeight: 600, color: T2 }}>Production generated</span>
          <span style={{ fontSize: 10.5, fontWeight: 700, color: T5, ...NUM }}>{money(production)}</span>
        </div>
      )}

      <div style={{ flex: 1 }} />

      <DocFooter
        counts={svcCounts}
        docName={kicker}
        right={`${emp.employee_name.toUpperCase()} · W${week} ${year}`}
        notes={hourly ? (
          <>
            Pay is based on days worked at the agreed daily rate, regardless of production.
            {hasPartial && ' Partial units (shown in orange) are jobs shared with another detailer.'}
            {' '}Please raise any discrepancy before the pay run.
          </>
        ) : (
          <>
            Paid per job — there is no base wage or weekly target.
            {hasPartial && ' Partial units (shown in orange) are jobs shared with another detailer.'}
            {' '}Pending jobs are not counted until approved. Please raise any discrepancy before the pay run.
          </>
        )}
      />
    </div>
  );
}
